import type { SetScoreRequest } from '@fall26/shared'

/**
 * What was typed into a score box, turned into a request.
 *
 * The box accepts a grade the way it was handed back: "47/50", "47" when the
 * item's point value is known, "94%", or a bare "94" when it is not. Blank
 * clears -- which is NOT the same as zero, and the difference decides whether
 * the item drags a course average down or sits outside it.
 *
 * No arithmetic happens here. "47/50" is sent as points and a denominator and
 * the server does the division, so the one place that turns points into a
 * percent is tested once rather than in every input that grows one.
 */
export type ParsedScore = { ok: true; body: SetScoreRequest; preview: string } | { ok: false; why: string }

export function parseScoreText(raw: string, maxPoints: number | null): ParsedScore {
  const text = raw.trim()
  if (text === '') return { ok: true, body: { pct: null }, preview: 'not graded' }

  const fraction = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(text)
  if (fraction) {
    const points = Number(fraction[1])
    const outOf = Number(fraction[2])
    if (outOf <= 0) return { ok: false, why: 'out of what? the denominator has to be above zero' }
    return { ok: true, body: { points, outOf }, preview: `${points} of ${outOf} → ${fmt((points / outOf) * 100)}%` }
  }

  const percent = /^(\d+(?:\.\d+)?)\s*%$/.exec(text)
  if (percent) {
    const pct = Number(percent[1])
    if (pct > 200) return { ok: false, why: 'a percent is at most 200' }
    return { ok: true, body: { pct }, preview: `${fmt(pct)}%` }
  }

  const bare = /^(\d+(?:\.\d+)?)$/.exec(text)
  if (!bare) return { ok: false, why: 'try 47/50, 47, or 94%' }
  const n = Number(bare[1])

  /*
   * A bare number is points when the item says what it is out of, and a
   * percent when it does not. That is the whole reason max_points exists:
   * "1" on a one-point attendance row must mean full marks, not 1%.
   */
  if (maxPoints !== null) {
    if (n > maxPoints)
      return { ok: false, why: `that is more than the ${fmt(maxPoints)} this is out of — add % if you meant a percent` }
    return { ok: true, body: { points: n }, preview: `${fmt(n)} of ${fmt(maxPoints)} → ${fmt((n / maxPoints) * 100)}%` }
  }
  if (n > 200) return { ok: false, why: 'a percent is at most 200 — for points, write 47/50' }
  return { ok: true, body: { pct: n }, preview: `${fmt(n)}%` }
}

/** "94", "93.5", "0.5" -- no trailing zeros, at most two decimals. */
export function fmt(n: number): string {
  return String(Math.round(n * 100) / 100)
}

/** The placeholder that tells you which of the three forms this box expects. */
export function scorePlaceholder(maxPoints: number | null): string {
  return maxPoints === null ? '47/50 or 94%' : `out of ${fmt(maxPoints)}`
}
